/**
 * multiSiteMapLayouts — the ALL-SITES alert board's MapLayoutApi (its GIS View).
 *
 * A floor plan belongs to ONE site, but an estate is a set of sites that each have their own,
 * so the cross-site GIS board is simply every site's plans in one picker: "HQ · Ground floor",
 * "HQ · Loading bay", "Pune · Gate". Pick one and the board shows that site's plan, its pins and
 * the alerts on them — the same board the per-site page mounts, fed by this adapter instead of
 * the selected-node one (mapLayouts.adapter).
 *
 * ── Ids are SITE-QUALIFIED ─────────────────────────────────────────────────────────────────
 * Two sites are two databases, so their layout ids can collide; every layout id crossing this
 * adapter is `nodeId::layoutId`, which is also how get() / imageUrl() know whose tunnel to ask.
 *
 * Camera ids in placements are qualified the same way (`siteCameraKey`) because the board
 * scopes its alert panel and live tiles by matching placement camera ids against the alert
 * feed — which on this page comes from createMultiSiteHexAlertsApi, already site-qualified.
 * A bare id here would match nothing and the plan would never light up.
 *
 * ── Read-only ──────────────────────────────────────────────────────────────────────────────
 * Editing a plan is a site's business (its pins are checked against that site's unique
 * constraint, its default is that site's default), so the page passes `canEdit={false}` and
 * every write here rejects with a message pointing at the per-site board.
 *
 * ── Offline sites ──────────────────────────────────────────────────────────────────────────
 * Every fan-out is allSettled: a site that doesn't answer simply contributes no plans. Only
 * when EVERY site fails does list() reject, so the board says "could not load" rather than
 * claiming the estate has no floor plans.
 */
import type {
  MapLayoutApi,
  MapLayoutDetail,
  MapLayoutSummary,
  MapPlacedCamera,
} from '@trinetra/shared';
import { getNodeMapLayout, getNodeMapLayouts, getNodeMapPlacedCameras } from '../services/api.service';
import { siteCameraKey, splitSiteCameraKey, type SiteRef } from './multiSiteHexAlerts.adapter';

/** Shown by every write: the cross-site board can look, not edit. */
export const CROSS_SITE_READ_ONLY =
  'Floor plans cannot be edited across sites — switch to that site to edit its plans.';

const readOnly = (): Promise<never> => Promise.reject(new Error(CROSS_SITE_READ_ONLY));

/** "HQ · Ground floor" — the picker lists every site's plans together, so each says whose. */
const siteLabel = (siteName: string | undefined, name: string) => (siteName ? `${siteName} · ${name}` : name);

/** Qualified layout id + updated_at → data URL. Module-level so a roster change (which builds a
 *  new adapter) doesn't re-ship plan images already on the page. One entry per layout version. */
const imageCache = new Map<string, string>();

/** Re-throw a proxy failure with the NODE's message (the cloud BE relays it verbatim). */
function rethrow(e: any): never {
  const body = e?.response?.data;
  throw new Error(body?.message || body?.error || e?.message || 'Request failed');
}

/**
 * Build the all-sites MapLayoutApi over `sites`.
 *
 * A NEW object per call and the board reloads when the api identity changes, so callers must
 * memoize it on the site roster (AllSitesAlertBoardPage keys on the node ids).
 */
export function createMultiSiteMapLayoutApi(sites: SiteRef[]): MapLayoutApi {
  const nameOf = new Map(sites.map((s) => [s.nodeId, s.name]));
  /** The estate's one default layout, decided by list(). The board always lists first. */
  let defaultId: string | null = null;

  /** Split a qualified layout id; a bare id has no site to route to. */
  const route = (layoutId: string) => {
    const { nodeId, cameraId: id } = splitSiteCameraKey(layoutId);
    if (!nodeId) throw new Error('That floor plan is not tied to a site');
    return { nodeId, id };
  };

  const qualifySummary = (nodeId: string, l: MapLayoutSummary): MapLayoutSummary => {
    const id = siteCameraKey(nodeId, l.id);
    return { ...l, id, name: siteLabel(nameOf.get(nodeId), l.name), is_default: id === defaultId };
  };

  const api: MapLayoutApi = {
    async list(): Promise<MapLayoutSummary[]> {
      if (sites.length === 0) return [];
      const settled = await Promise.allSettled(
        sites.map(async (site) => {
          const rows: any = await getNodeMapLayouts(site.nodeId);
          const list: MapLayoutSummary[] = Array.isArray(rows) ? rows : [];
          return { nodeId: site.nodeId, list: [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) };
        }),
      );
      const ok = settled.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
      if (ok.length === 0) {
        const first = settled.find((r): r is PromiseRejectedResult => r.status === 'rejected');
        rethrow(first?.reason);
      }

      // One default for the estate: the first site (roster order) that has any plans opens on
      // its own default, else on its first plan.
      const opener = ok.find((s) => s.list.length > 0);
      const pick = opener ? opener.list.find((l) => l.is_default) || opener.list[0] : null;
      defaultId = opener && pick ? siteCameraKey(opener.nodeId, pick.id) : null;

      return ok.flatMap((s) => s.list.map((l) => qualifySummary(s.nodeId, l)));
    },

    async get(layoutId: string): Promise<MapLayoutDetail> {
      const { nodeId, id } = route(layoutId);
      const detail: any = await getNodeMapLayout(nodeId, id).catch(rethrow);
      // Cache the image here: this is the only response that carries the bytes, and the board
      // calls imageUrl() right after with the same layout.
      if (detail?.image_data && detail?.image_mime) {
        imageCache.set(`${layoutId}:${detail.updated_at ?? ''}`, `data:${detail.image_mime};base64,${detail.image_data}`);
      }
      const placements: any[] = Array.isArray(detail?.placements) ? detail.placements : [];
      return {
        ...detail,
        ...qualifySummary(nodeId, detail as MapLayoutSummary),
        placements: placements.map((p) => ({ ...p, camera_id: siteCameraKey(nodeId, p.camera_id) })),
      } as MapLayoutDetail;
    },

    async imageUrl(layout: MapLayoutSummary): Promise<string> {
      const hit = imageCache.get(`${layout.id}:${layout.updated_at ?? ''}`);
      if (hit) return hit;
      // Fallback for a summary handed over without a detail read. By name, not `this`: the
      // board passes this method around as a plain function.
      const detail: any = await api.get(layout.id);
      if (!detail?.image_data) throw new Error('That layout has no image');
      return `data:${detail.image_mime};base64,${detail.image_data}`;
    },

    /** Qualified camera id → the (qualified, site-labelled) layout that owns it. */
    async placedCameras(): Promise<Record<string, MapPlacedCamera>> {
      const settled = await Promise.allSettled(
        sites.map(async (site) => ({ nodeId: site.nodeId, map: (await getNodeMapPlacedCameras(site.nodeId)) as any })),
      );
      const out: Record<string, MapPlacedCamera> = {};
      for (const r of settled) {
        if (r.status !== 'fulfilled' || !r.value.map || typeof r.value.map !== 'object') continue;
        const { nodeId, map } = r.value;
        for (const [cameraId, placed] of Object.entries(map as Record<string, MapPlacedCamera>)) {
          out[siteCameraKey(nodeId, cameraId)] = {
            ...placed,
            layout_id: siteCameraKey(nodeId, placed.layout_id),
            layout_name: siteLabel(nameOf.get(nodeId), placed.layout_name),
          };
        }
      }
      return out;
    },

    create: readOnly,
    update: readOnly,
    delete: readOnly,
    setPlacements: readOnly,
  };
  return api;
}
